/**
 * Pointer adapter between the scene camera and StPageFlip.
 *
 * WHY THIS EXISTS
 * StPageFlip's own input layer converts pointer coordinates with
 * `clientX - distElement.getBoundingClientRect().left` (UI.ts:160) while all
 * of its page geometry is computed from `offsetWidth`/`offsetHeight`
 * (Render.ts:207). The first is affected by CSS transforms, the second is not,
 * so under a scaled camera the page lags the cursor by the scale factor.
 *
 * We therefore run the engine with `useMouseEvents: false` and feed it
 * book-local points ourselves through its public API — `startUserTouch`,
 * `userMove`, `userStop`. Undoing the camera through the ratio of the
 * transformed rect to the layout box is exact for scale+translate and needs no
 * DOMMatrix; it also re-derives itself for free when `autoSize` relayouts.
 *
 * Turning the engine's handlers off also removes its touch handling, so swipe
 * detection and the "don't hijack the gesture until it is clearly horizontal"
 * rule live here now.
 */

const SWIPE_TIMEOUT = 250;
const SWIPE_DISTANCE = 40;
const HORIZONTAL_THRESHOLD = 10;

function isCoarse(event) {
  return event.pointerType === 'touch' || event.pointerType === 'pen';
}

/**
 * @param {object} options
 * @param {HTMLElement} options.scene - element receiving pointer events
 * @param {import('page-flip').PageFlip} options.pageFlip
 * @param {(state: { overPage: boolean, grabbing: boolean }) => void} [options.onPointerState]
 * @param {(progress: number) => void} [options.onFlipProgress]
 * @param {(event: PointerEvent) => void} [options.onPointerDown]
 * @param {() => void} [options.onPrevAtStart] - a rightward drag on the first
 *   interior page. The engine has no leaf to fold there (the cover is not one
 *   of its pages), so the gesture would otherwise die silently; the owner
 *   closes the book instead.
 */
export function createPointerBridge({
  scene,
  pageFlip,
  onPointerState,
  onFlipProgress,
  onPointerDown,
  onPrevAtStart
}) {
  const dist = pageFlip.getUI().getDistElement();

  let activePointerId = null;
  let gestureStart = null;
  let engagedGesture = false;
  let gestureLocked = false;
  let overPage = false;
  let lastPoint = null;
  // Every pointer currently down on the scene. A second one means a pinch, and
  // the browser owns that gesture (`touch-action: pinch-zoom`): the leaf has to
  // be let go, or it would follow whichever finger reports a move first.
  const livePointers = new Set();

  /** Convert client coordinates into the engine's untransformed local space. */
  function toBookPoint(clientX, clientY) {
    const rect = dist.getBoundingClientRect();
    const scaleX = rect.width / dist.offsetWidth || 1;
    const scaleY = rect.height / dist.offsetHeight || 1;

    return {
      x: (clientX - rect.left) / scaleX,
      y: (clientY - rect.top) / scaleY
    };
  }

  function isInsideBook(point) {
    const bounds = pageFlip.getBoundsRect();
    return (
      point.x >= bounds.left &&
      point.x <= bounds.left + bounds.width &&
      point.y >= bounds.top &&
      point.y <= bounds.top + bounds.height
    );
  }

  function publishState(grabbing) {
    onPointerState?.({ overPage, grabbing });
  }

  function reportProgress(point) {
    if (!onFlipProgress) return;
    const bounds = pageFlip.getBoundsRect();
    if (!bounds.width) return;
    const centre = bounds.left + bounds.width / 2;
    onFlipProgress(Math.max(-1, Math.min(1, (point.x - centre) / (bounds.width / 2))));
  }

  function onPointerMoveHover(event) {
    if (activePointerId !== null) return;
    const next = isInsideBook(toBookPoint(event.clientX, event.clientY));
    if (next === overPage) return;
    overPage = next;
    publishState(false);
  }

  function onPointerLeave() {
    if (activePointerId !== null || !overPage) return;
    overPage = false;
    publishState(false);
  }

  /** Drop the leaf where it is, without a swipe verdict. */
  function abortGesture() {
    if (engagedGesture && lastPoint) pageFlip.userStop(lastPoint);
    if (activePointerId !== null && scene.hasPointerCapture?.(activePointerId)) {
      scene.releasePointerCapture(activePointerId);
    }
    activePointerId = null;
    gestureStart = null;
    engagedGesture = false;
    gestureLocked = false;
    onFlipProgress?.(0);
    publishState(false);
  }

  function onDown(event) {
    if (event.button !== undefined && event.button !== 0) return;

    livePointers.add(event.pointerId);
    if (livePointers.size > 1) {
      abortGesture();
      return;
    }

    // Interactive elements on a page (QR hotspots) keep their native
    // semantics: a press on them must not also start dragging the leaf.
    if (event.target instanceof Element && event.target.closest('a, button')) return;

    onPointerDown?.(event);

    const point = toBookPoint(event.clientX, event.clientY);
    if (!isInsideBook(point)) return;

    activePointerId = event.pointerId;
    gestureStart = { point, time: Date.now() };
    gestureLocked = false;

    // A touch gesture may still turn out to be a vertical page scroll, so the
    // engine is only told about it once the movement reads as horizontal.
    engagedGesture = !isCoarse(event);

    if (engagedGesture) {
      scene.setPointerCapture(event.pointerId);
      pageFlip.startUserTouch(point);
      overPage = true;
      publishState(true);
      event.preventDefault();
    }
  }

  function onMove(event) {
    if (event.pointerId !== activePointerId) {
      onPointerMoveHover(event);
      return;
    }

    const point = toBookPoint(event.clientX, event.clientY);
    lastPoint = point;

    if (!engagedGesture) {
      if (gestureLocked) return;

      const dx = Math.abs(point.x - gestureStart.point.x);
      const dy = Math.abs(point.y - gestureStart.point.y);

      // Once a gesture has committed to the vertical it belongs to the page
      // scroll for good; it must not be able to snag the book later on.
      if (dy > HORIZONTAL_THRESHOLD && dy > dx) {
        gestureLocked = true;
        return;
      }

      if (dx < HORIZONTAL_THRESHOLD || dx <= dy) return;

      engagedGesture = true;
      scene.setPointerCapture(event.pointerId);
      pageFlip.startUserTouch(gestureStart.point);
      publishState(true);
    }

    pageFlip.userMove(point, isCoarse(event));
    reportProgress(point);
    event.preventDefault();
  }

  function onUp(event) {
    livePointers.delete(event.pointerId);
    if (event.pointerId !== activePointerId) return;

    const point = toBookPoint(event.clientX, event.clientY);
    lastPoint = point;
    const start = gestureStart;

    activePointerId = null;
    gestureStart = null;

    if (scene.hasPointerCapture?.(event.pointerId)) {
      scene.releasePointerCapture(event.pointerId);
    }

    if (engagedGesture) {
      pageFlip.userStop(point, detectSwipe(start, point));
      detectPrevAtStart(start, point);
    }

    engagedGesture = false;
    gestureLocked = false;
    onFlipProgress?.(0);
    overPage = isInsideBook(point);
    publishState(false);
  }

  function onCancel(event) {
    livePointers.delete(event.pointerId);
    if (event.pointerId !== activePointerId) return;
    const point = toBookPoint(event.clientX, event.clientY);

    activePointerId = null;
    gestureStart = null;

    if (engagedGesture) pageFlip.userStop(point);
    engagedGesture = false;
    gestureLocked = false;
    onFlipProgress?.(0);
    publishState(false);
  }

  /**
   * A committed rightward pull that started on the left page while the engine
   * is on its first page. There is nothing for the engine to fold backwards
   * onto, so the intent — back to the cover — is reported to the owner.
   */
  function detectPrevAtStart(start, point) {
    if (!onPrevAtStart || !start || pageFlip.getCurrentPageIndex() !== 0) return;

    const bounds = pageFlip.getBoundsRect();
    const dx = point.x - start.point.x;
    const dy = Math.abs(point.y - start.point.y);
    if (dx < Math.max(48, bounds.width * 0.08) || dy > dx) return;

    // In landscape the pull has to begin on the left page; a lone portrait
    // page is the whole book, so anywhere on it counts.
    if (
      pageFlip.getOrientation() === 'landscape' &&
      start.point.x > bounds.left + bounds.width / 2
    ) {
      return;
    }

    onPrevAtStart();
  }

  /**
   * Replaces the swipe detection lost with the engine's own touch handlers:
   * a quick horizontal flick flips without dragging the page all the way.
   */
  function detectSwipe(start, point) {
    if (!start) return false;
    const dx = point.x - start.point.x;
    const dy = Math.abs(point.y - start.point.y);
    const quick = Date.now() - start.time < SWIPE_TIMEOUT;

    if (!quick || Math.abs(dx) <= SWIPE_DISTANCE || dy >= SWIPE_DISTANCE * 2) return false;

    const bounds = pageFlip.getBoundsRect();
    const corner = start.point.y < bounds.top + bounds.height / 2 ? 'top' : 'bottom';

    if (dx > 0) pageFlip.flipPrev(corner);
    else pageFlip.flipNext(corner);

    return true;
  }

  scene.addEventListener('pointerdown', onDown);
  scene.addEventListener('pointermove', onMove);
  scene.addEventListener('pointerup', onUp);
  scene.addEventListener('pointercancel', onCancel);
  scene.addEventListener('pointerleave', onPointerLeave);

  return {
    isOverPage() {
      return overPage;
    },

    destroy() {
      scene.removeEventListener('pointerdown', onDown);
      scene.removeEventListener('pointermove', onMove);
      scene.removeEventListener('pointerup', onUp);
      scene.removeEventListener('pointercancel', onCancel);
      scene.removeEventListener('pointerleave', onPointerLeave);
    }
  };
}
